import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, BadgeCheck } from "lucide-react";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import { sectors, sectorCodes } from "../mock";
import { iconMap } from "../lib/iconMap";

export default function Sectors() {
  return (
    <div>
      <PageHeader
        title="Sectors We Serve"
        crumb="Sectors"
        subtitle="Our B-BBEE consulting expertise spans every major South African industry — from mining and manufacturing to financial services, ICT and beyond."
      />

      {/* Intro */}
      <section className="py-16">
        <div className="container-x max-w-3xl text-center">
          <Reveal>
            <div className="eyebrow justify-center mb-4" style={{ display: "inline-flex" }}>Industries & Sector Codes</div>
            <p className="text-slate-600 leading-relaxed text-lg">
              B-BBEE compliance is not one-size-fits-all. Different industries are measured against
              different sector codes and priorities. Whatever your sector, we tailor a strategy that
              fits your business and the code that applies to it.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Sector grid */}
      <section className="pb-20">
        <div className="container-x">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {sectors.map((s, idx) => {
              const Icon = iconMap[s.icon];
              return (
                <Reveal key={s.title} delay={(idx % 4) * 70}>
                  <div className="group card-lift bg-white rounded-2xl p-6 border border-slate-100 h-full">
                    <div className="w-13 h-13 w-[52px] h-[52px] rounded-xl bg-[#0B1F3A] grid place-items-center text-[#D4AF37] mb-4 group-hover:bg-[#D4AF37] group-hover:text-[#0B1F3A] transition-colors">
                      {Icon ? <Icon size={24} /> : null}
                    </div>
                    <h3 className="font-head font-semibold text-[#0B1F3A] text-[17px] mb-2 leading-snug">{s.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{s.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sector codes band */}
      <section className="py-20 bg-[#0B1F3A]">
        <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="eyebrow mb-4" style={{ color: "#D4AF37" }}>Codes We Work With</div>
            <h2 className="font-head text-3xl md:text-4xl font-bold text-white leading-tight">
              Deep expertise across every B-BBEE sector code
            </h2>
            <p className="mt-5 text-slate-300 leading-relaxed">
              We stay current with the Generic Codes of Good Practice and every gazetted sector charter,
              so your scorecard is always measured correctly and optimised for your industry.
            </p>
            <Link to="/pricing-calculator" className="btn-gold inline-flex items-center gap-2 px-6 py-3 rounded-lg mt-8">
              Request a Tailored Quote <ArrowRight size={17} />
            </Link>
          </Reveal>
          <Reveal delay={120}>
            <div className="grid sm:grid-cols-2 gap-3">
              {sectorCodes.map((code) => (
                <div key={code} className="flex items-start gap-2.5 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                  <BadgeCheck size={19} className="text-[#D4AF37] shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-200">{code}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Not sure band */}
      <section className="py-16">
        <div className="container-x">
          <div className="rounded-3xl bg-[#F6F7F9] border border-slate-100 p-10 md:p-14 text-center">
            <h2 className="font-head text-2xl md:text-3xl font-bold text-[#0B1F3A] max-w-2xl mx-auto">
              Not sure which sector code applies to your business?
            </h2>
            <p className="mt-4 text-slate-600 max-w-xl mx-auto">
              Talk to one of our specialists — we'll quickly identify the right code and the fastest path to a stronger scorecard.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="btn-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-lg">
                Speak to a Specialist <ArrowRight size={18} />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg border border-slate-200 text-[#0B1F3A] font-semibold hover:border-[#D4AF37] transition-colors"
              >
                View Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
